import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { css } from '@linaria/core';
import clsx from 'clsx';
import { Icon } from '@cbhq/cds-web/icons/Icon';
import { Box, HStack, VStack } from '@cbhq/cds-web/layout';
import { deviceBreakpoints } from '@cbhq/cds-web/layout/breakpoints';
import { Pressable } from '@cbhq/cds-web/system/Pressable';
import { TextBody, TextHeadline, TextTitle1 } from '@cbhq/cds-web/typography';
import {
  ActionType,
  AnalyticsEventImportance,
  ComponentType,
  logEvent,
} from '@cbhq/client-analytics';

import { useSimpleBreakpoints } from ':cloud/hooks/useSimpleBreakpoints';
import { lockedColor, unlockedColor } from ':cloud/widgets/buildQuest/assets/colors';
import { useBuildQuestContext } from ':cloud/widgets/buildQuest/BuildQuestProvider';

export type CarouselItem = {
  id: number;
  name: string;
  imageUrl: string;
  isLocked: boolean;
  requiredLevel: number;
};

interface CarouselProps {
  items: CarouselItem[];
  claimPrizeModalOn: () => void;
}

const SCALE = 1.2;

// Desktop constants
const ITEM_WIDTH_DESKTOP = 244;
const GAP_DESKTOP = 24;

// Mobile constants
const ITEM_WIDTH_MOBILE = 220;
const GAP_MOBILE = 24;

const hStackStyle = css`
  transition: transform 0.5s cubic-bezier(0.6, 0, 0.15, 1);
`;

const noTransition = css`
  transition: none !important;
`;

const imgStyle = css`
  max-height: 160px;
  object-fit: contain;
  width: auto;
  max-width: 90%;

  @media (max-width: ${deviceBreakpoints.phoneLandscape}px) {
    max-height: 160px;
  }
`;

const currentItemStyle = css`
  cursor: pointer;
  transform: scale(${SCALE});
  transition:
    opacity 0.3s,
    transform 0.3s;
  margin-left: 24px;
  margin-right: 24px;
  overflow: hidden;
`;

const otherItemStyle = css`
  cursor: pointer;
  transform: scale(1);
  transition:
    opacity 0.3s,
    transform 0.3s;
  overflow: hidden;
`;

export function Carousel({ items, claimPrizeModalOn }: CarouselProps): React.ReactElement {
  const [currentItem, setCurrentItem] = useState<CarouselItem>(items[0]);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [isInitialMount, setIsInitialMount] = useState<boolean>(true);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [dragStartX, setDragStartX] = useState<number | null>(null);
  const [dragCurrentX, setDragCurrentX] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const { isPhone } = useSimpleBreakpoints();
  const { togglePrizesModalOff } = useBuildQuestContext();

  const ITEM_WIDTH = useMemo(() => (isPhone ? ITEM_WIDTH_MOBILE : ITEM_WIDTH_DESKTOP), [isPhone]);
  const GAP = useMemo(() => (isPhone ? GAP_MOBILE : GAP_DESKTOP), [isPhone]);
  const STACK_GAP = useMemo(() => (isPhone ? 4 : 5), [isPhone]);
  const CAROUSEL_ITEM_HEIGHT = useMemo(() => (isPhone ? 240 : 260), [isPhone]);

  const centerSelectedItem = useCallback(
    (index: number) => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        const selectedItemElement = itemRefs.current[items[index].id];
        const itemLeftPosition = selectedItemElement?.offsetLeft || 0;
        const centerOffset = containerWidth / 2 - (ITEM_WIDTH * SCALE) / 2 + GAP;
        const translateXValue = centerOffset - itemLeftPosition;
        containerRef.current.style.transform = `translateX(${translateXValue}px)`;
      }
    },
    [ITEM_WIDTH, GAP, items],
  );

  const handleClick = useCallback(
    (item: CarouselItem, index: number) => {
      setCurrentItem(item);
      setCurrentIndex(index);
      centerSelectedItem(index);
    },
    [centerSelectedItem],
  );

  const handleDragStart = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    setIsDragging(true);
    setDragStartX(e.touches[0].clientX);
  }, []);

  const handleDragMove = useCallback(
    (e: React.TouchEvent<HTMLDivElement>) => {
      if (isDragging) {
        setDragCurrentX(e.touches[0].clientX);
      }
    },
    [isDragging],
  );

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
    setDragStartX(null);
    setDragCurrentX(null);

    // get the drag distance
    const dragDistance = (dragCurrentX || 0) - (dragStartX || 0);
    // item width including gap to clear
    const effectiveItemWidth = ITEM_WIDTH + GAP;
    const indexChange = Math.round(dragDistance / effectiveItemWidth);
    const newIndex = Math.max(0, Math.min(items.length - 1, currentIndex - indexChange));
    setCurrentIndex(newIndex);
    setCurrentItem(items[newIndex]);
    centerSelectedItem(newIndex);
  }, [dragCurrentX, dragStartX, ITEM_WIDTH, GAP, items, currentIndex, centerSelectedItem]);

  const handlePress = useCallback(() => {
    if (currentItem.isLocked) {
      logEvent(
        'quest_prize_locked_click',
        {
          action: ActionType.click,
          componentType: ComponentType.button,
        },
        AnalyticsEventImportance.high,
      );
      togglePrizesModalOff();
    } else {
      claimPrizeModalOn();
      logEvent(
        'quest_prize_claim_click',
        {
          action: ActionType.click,
          componentType: ComponentType.button,
        },
        AnalyticsEventImportance.high,
      );
    }
  }, [currentItem.isLocked, claimPrizeModalOn, togglePrizesModalOff]);

  useEffect(() => {
    if (containerRef.current && currentItem) {
      centerSelectedItem(currentIndex);
      if (isInitialMount) {
        setIsInitialMount(false);
      }
    }
  }, [currentIndex, isInitialMount, currentItem, centerSelectedItem]);

  useEffect(() => {
    if (isDragging && dragStartX !== null && dragCurrentX !== null) {
      const dragDistance = dragCurrentX - dragStartX;
      const initialTranslateX = -currentIndex * (ITEM_WIDTH + GAP);
      const translateX = initialTranslateX + dragDistance;
      containerRef.current?.style.setProperty('transform', `translateX(${translateX}px)`);
    }
  }, [isDragging, dragStartX, dragCurrentX, currentIndex, ITEM_WIDTH, GAP]);

  useEffect(() => {
    if (containerRef.current && currentItem) {
      centerSelectedItem(currentIndex);
      if (isInitialMount) {
        setIsInitialMount(false);
      }
    }
  }, [currentIndex, isInitialMount, currentItem, centerSelectedItem]);

  useEffect(() => {
    if (isDragging && dragStartX !== null && dragCurrentX !== null) {
      const dragDistance = dragCurrentX - dragStartX;
      const initialTranslateX = -currentIndex * (ITEM_WIDTH + GAP);
      const translateX = initialTranslateX + dragDistance;
      containerRef.current?.style.setProperty('transform', `translateX(${translateX}px)`);
    }
  }, [isDragging, dragStartX, dragCurrentX, currentIndex, ITEM_WIDTH, GAP]);

  return (
    <VStack width="100%" position="relative" gap={8}>
      <HStack
        gap={STACK_GAP}
        ref={containerRef}
        justifyContent="center"
        className={clsx(hStackStyle, { [noTransition]: isInitialMount })}
        onTouchStart={handleDragStart}
        onTouchMove={handleDragMove}
        onTouchEnd={handleDragEnd}
        key="carousel-container"
      >
        {items.map((item, index) => {
          const isCurrent = index === currentIndex;

          return (
            <Box
              key={item.id}
              ref={(el) => {
                itemRefs.current[item.id] = el as HTMLDivElement;
              }}
              onClick={() => handleClick(item, index)}
              height={CAROUSEL_ITEM_HEIGHT}
              background="backgroundInverse"
              borderRadius="roundedLarge"
              justifyContent="center"
              alignItems="center"
              opacity={isCurrent ? 1 : 0.6}
              width={ITEM_WIDTH}
              flexShrink={0}
              position="relative"
              className={clsx(isCurrent ? currentItemStyle : otherItemStyle, {
                [noTransition]: isInitialMount,
              })}
            >
              {isCurrent && (
                <Box
                  position="absolute"
                  top={8}
                  left={0}
                  right={0}
                  zIndex={999}
                  width="100%"
                  justifyContent="center"
                >
                  <TextBody as="p" color="currentColor">
                    {item.name}
                  </TextBody>
                </Box>
              )}
              <img src={item.imageUrl} alt={item.name} width="100%" className={imgStyle} />
            </Box>
          );
        })}
      </HStack>
      <VStack alignItems="center" gap={4} spacingBottom={4}>
        <VStack alignItems="center" gap={1}>
          <HStack alignItems="center" gap={1}>
            <Icon
              name={currentItem.isLocked ? 'lock' : 'shield'}
              size="m"
              dangerouslySetColor={currentItem.isLocked ? lockedColor : unlockedColor}
            />
            <TextTitle1
              as="p"
              dangerouslySetColor={currentItem.isLocked ? lockedColor : unlockedColor}
            >
              {currentItem.isLocked ? 'Locked' : 'Unlocked'}
            </TextTitle1>
          </HStack>
          {currentItem.isLocked && (
            <HStack alignItems="center">
              <TextBody as="p" color="foregroundMuted">
                Get to level {currentItem.requiredLevel} to unlock
              </TextBody>
            </HStack>
          )}
        </VStack>
        <Pressable
          as="button"
          background="backgroundInverse"
          borderRadius="roundedFull"
          onPress={handlePress}
        >
          <Box spacingHorizontal={4} spacingVertical={2}>
            <TextHeadline as="p" dangerouslySetColor="var(--background)">
              {currentItem.isLocked ? 'Get more exp' : 'Claim now'}
            </TextHeadline>
          </Box>
        </Pressable>
      </VStack>
    </VStack>
  );
}
